import { Component, type ErrorInfo, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// The last line of defence.
//
// React unmounts the whole tree when a render throws, and an unmounted tree on
// a dark page is a black screen with nothing to click and nothing to read. That
// is the worst possible failure: the player cannot tell a crash from a hang
// from a lost connection, and neither can anyone trying to fix it. Whatever
// went wrong, it should say so and offer the way out.
// ---------------------------------------------------------------------------

interface State {
  error: Error | null;
  info: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, info: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep it in the console too, where a stack trace is actually usable.
    console.error('Trading Blows crashed', error, info);
    this.setState({ info: (info.componentStack ?? '').split('\n').slice(0, 6).join('\n') });
  }

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="centered">
        <div className="card raised">
          <h1>Something broke</h1>
          <p className="sub" style={{ marginBottom: 14 }}>
            The display crashed. Your match is held on the server, so reloading
            should put you straight back into it where you left off.
          </p>
          <div className="log small" style={{ marginBottom: 14 }}>
            <div className="log-entry tone-warn mono">{error.message || String(error)}</div>
            {info && (
              <div className="log-entry mono" style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--fs-xs)' }}>
                {info}
              </div>
            )}
          </div>
          <div className="row">
            <button className="btn primary grow" onClick={() => location.reload()}>
              Reload
            </button>
            <button className="btn" onClick={() => this.setState({ error: null, info: '' })}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
